import { useState } from 'react';

import { StarIcon, BookmarkIcon, CopyIcon, CheckIcon } from './icons';
import MessageContent from './chat/MessageContent';
import ExpandableBubble from './chat/ExpandableBubble';

export default function ChatMessage({ message, index, onTogglePersistent, onSavePrompt, isStreaming = false }) {
  const { role, content, persistent, timestamp, toolStatus, attachment, toolCalls } = message;
  const isUser  = role === 'user';
  const isError = role === 'error';
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      const el = document.createElement('textarea');
      el.value = content;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const bubbleClass =
    isUser      ? 'bubble-user'      :
    isError     ? 'bubble-error'     :
    persistent  ? 'bubble-persistent':
                  'bubble-assistant';

  const badge = persistent && !isUser && !isError
    ? <span className="saved-badge">saved</span>
    : null;

  const ActionButtons = () => (
    <div className="msg-actions">
      <button
        title={persistent ? 'Remove from memory' : 'Save to long-term memory'}
        onClick={() => onTogglePersistent(index)}
        className={`msg-action-btn ${persistent ? 'action-active-star' : ''}`}
      >
        <StarIcon filled={persistent} />
      </button>
      <button
        title="Save to prompts"
        onClick={() => onSavePrompt(content)}
        className="msg-action-btn"
      >
        <BookmarkIcon />
      </button>
      {!isError && (
        <button
          title={copied ? 'Copied!' : 'Copy to clipboard'}
          onClick={handleCopy}
          className={`msg-action-btn ${copied ? 'action-active-copy' : ''}`}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>
      )}
    </div>
  );

  // ── Tool status line — shown while tools are running ──────────────────────
  const ToolStatusLine = () => (
    <div className="tool-status-line">
      <span>{toolStatus}</span>
    </div>
  );

  // ── Tool annotation blocks (rendered before assistant text) ───────────────
  const ToolBlocks = () => {
    if (!toolCalls || toolCalls.length === 0) return null;
    return toolCalls.map((tool, i) => {
      const statusClass = tool.status === 'ok' ? 'ok' : tool.status === 'err' ? 'err' : '';
      return (
        <div className={`tool-block ${statusClass}`} key={tool.id || i}>
          <div className="tool-block-signature">
            ⎿&nbsp;
            <span className="tool-name">{tool.name}</span>
            <span className="tool-paren">(</span>
            <span className="tool-args">{tool.argsPreview || ''}</span>
            <span className="tool-paren">)</span>
          </div>
          <div className="tool-block-result">
            {tool.status === 'ok'  && <span className="tool-ok">✓ {tool.summary}</span>}
            {tool.status === 'err' && <span className="tool-err">✗ {tool.error}</span>}
            {tool.status === 'run' && <span className="tool-running">⟳ running…</span>}
          </div>
        </div>
      );
    });
  };

  const [errorExpanded, setErrorExpanded] = useState(false);

  const bubbleContent = isError ? (
    <div className="error-bubble-content">
      <div className="error-bubble-header">
        <span className="error-bubble-icon">✗</span>
        <span className="error-bubble-summary">Error getting response from AI provider. Try again</span>
        {content && (
          <button
            className="error-expand-btn"
            onClick={() => setErrorExpanded(x => !x)}
            title={errorExpanded ? 'Hide details' : 'Show details'}
          >
            {errorExpanded ? '▲' : '▼'}
          </button>
        )}
      </div>
      {content && errorExpanded && <div className="error-bubble-detail">{content}</div>}
    </div>
  ) : toolStatus && !content ? (
    // Tools running, no text yet — show status only
    <ToolStatusLine />
  ) : (
    <>
      {toolStatus && <ToolStatusLine />}
      {attachment && (
        <div className="attachment-chip">
          File: {attachment}
        </div>
      )}
      {content && <MessageContent content={content} isStreaming={isStreaming} />}
    </>
  );

  // ── User turn ─────────────────────────────────────────────────────────────
  if (isUser) {
    return (
      <div className="message-row row-user">
        <div className="message-bubble bubble-user">
          {bubbleContent}
        </div>
        <ActionButtons />
      </div>
    );
  }

  // ── Assistant / error turn ────────────────────────────────────────────────
  return (
    <div className={`message-row ${isError ? 'row-error' : 'row-assistant'}`}>
      {/* Tool annotation blocks above assistant text */}
      <ToolBlocks />

      <ExpandableBubble
        bubbleClass={bubbleClass}
        badge={badge}
        timestamp={timestamp}
        isUser={false}
      >
        {bubbleContent}
      </ExpandableBubble>

      <ActionButtons />
    </div>
  );
}
